import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import ManageTagsDialog from 'components/@home/@settings/Tabs/Agents/ManageTagsDialog';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import RefreshIcon from 'mdi-react/RefreshIcon';
import AutorenewIcon from 'mdi-react/AutorenewIcon';
import TagOutlineIcon from 'mdi-react/TagOutlineIcon';
import AgentTag from 'services/api/AgentTag';
import ViewColumnOutlineIcon from 'mdi-react/ViewColumnOutlineIcon';
import { arrayOf, func, object } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { I18n } from 'utils/i18n';

const COLUMNS = [
  { id: 'name', labelKey: 'TagsColumnName' },
  { id: 'category', labelKey: 'TagsColumnCategory' },
  { id: 'description', labelKey: 'TagsColumnDescription' },
  { id: 'type', labelKey: 'TagsColumnType' },
];

const compare = (a, b, key) => {
  const av = (a?.[key] ?? '').toString().toLowerCase();
  const bv = (b?.[key] ?? '').toString().toLowerCase();
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
};

const TagsPanel = ({ agent, allTags = [], onSaveAgent, onReloadTags = null }) => {
  const navigate = useNavigate();
  const { flow, authCompany } = useAgentEdit();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState({ id: 'name', dir: 'asc' });
  const [visibleCols, setVisibleCols] = useState({
    name: true,
    category: true,
    description: true,
    type: true,
  });
  const [colsAnchor, setColsAnchor] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [reevaluating, setReevaluating] = useState(null);

  const handleReevaluate = async (tag, e) => {
    e.stopPropagation();
    setReevaluating(String(tag._id));
    try {
      await AgentTag.reevaluate(tag._id);
    } finally {
      setReevaluating(null);
    }
  };

  const refresh = () => {
    if (onReloadTags) onReloadTags();
  };

  const attachedIds = useMemo(() => (agent?.tagIds || []).map(String), [agent?.tagIds]);

  const attachedTags = useMemo(() => {
    const idSet = new Set(attachedIds);
    return (allTags || []).filter(t => idSet.has(String(t._id)));
  }, [allTags, attachedIds]);

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? attachedTags.filter(
          t =>
            (t.name || '').toLowerCase().includes(q) ||
            (t.category || '').toLowerCase().includes(q) ||
            (t.description || '').toLowerCase().includes(q),
        )
      : attachedTags;
    const sorted = [...base].sort((a, b) => compare(a, b, sortBy.id));
    return sortBy.dir === 'desc' ? sorted.reverse() : sorted;
  }, [attachedTags, search, sortBy]);

  const setSort = colId => {
    setSortBy(prev =>
      prev.id === colId
        ? { id: colId, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { id: colId, dir: 'asc' },
    );
  };

  const persistTagIds = nextIds => {
    onSaveAgent({ tagIds: nextIds });
  };

  const openTag = id => {
    if (!flow?._id) return;
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'tags', tagId: id }));
  };

  const openCreate = () => openTag('new');
  const openEdit = tag => openTag(tag._id);

  const renderCell = (tag, colId) => {
    if (colId === 'name') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TagOutlineIcon size={14} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
            }}
          >
            {tag.name}
          </Typography>
        </Box>
      );
    }
    if (colId === 'category') return tag.category || 'General';
    if (colId === 'description') return tag.description || '—';
    if (colId === 'type') {
      return tag.type === 'rule'
        ? I18n.t('AgenticChatFlow.TagsTypeRule')
        : I18n.t('AgenticChatFlow.TagsTypeInstruction');
    }
    return null;
  };

  const renderTable = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'background.paper',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.filter(c => visibleCols[c.id]).map(col => (
              <TableCell key={col.id}>
                <TableSortLabel
                  active={sortBy.id === col.id}
                  direction={sortBy.dir}
                  onClick={() => setSort(col.id)}
                >
                  {I18n.t(`AgenticChatFlow.${col.labelKey}`)}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="right">{I18n.t('AgenticChatFlow.TagsColumnActions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTags.map(tag => (
            <TableRow key={tag._id} sx={{ cursor: 'pointer' }} hover onClick={() => openEdit(tag)}>
              {COLUMNS.filter(c => visibleCols[c.id]).map(col => (
                <TableCell key={col.id}>{renderCell(tag, col.id)}</TableCell>
              ))}
              <TableCell align="right">
                {tag.type === 'rule' && (
                  <Tooltip title={I18n.t('AgenticChatFlow.TagsReevaluate')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={reevaluating === String(tag._id)}
                        onClick={e => handleReevaluate(tag, e)}
                      >
                        <AutorenewIcon size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const renderEmpty = () => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        color: 'text.secondary',
        p: 6,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 56,
          height: 56,
          borderRadius: '16px',
          bgcolor: 'background.default',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <TagOutlineIcon size={26} />
      </Box>
      <Typography variant="body2">{I18n.t('AgenticChatFlow.TagsEmptyHint')}</Typography>
      <Button
        variant="contained"
        color="primary"
        disableElevation
        startIcon={<PlusIcon size={16} />}
        onClick={openCreate}
      >
        {I18n.t('AgenticChatFlow.TagsNew')}
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        px: 4,
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(22), fontWeight: 700 }}>
          {I18n.t('AgenticChatFlow.TagsAllTitle')}
        </Typography>
        <Tooltip title={I18n.t('AgenticChatFlow.TagsRefresh')}>
          <IconButton size="small" onClick={refresh}>
            <RefreshIcon size={18} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<CogOutlineIcon size={16} />}
            onClick={() => setManageOpen(true)}
          >
            {I18n.t('AgenticChatFlow.TagsManage')}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<PlusIcon size={16} />}
            onClick={openCreate}
          >
            {I18n.t('AgenticChatFlow.TagsNew')}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <TextField
            sx={{ minWidth: 220 }}
            size="small"
            placeholder={I18n.t('AgenticChatFlow.TagsSearchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon size={16} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<ViewColumnOutlineIcon size={16} />}
            onClick={e => setColsAnchor(e.currentTarget)}
          >
            {I18n.t('AgenticChatFlow.TagsColumns')}
          </Button>
          <Menu
            anchorEl={colsAnchor}
            open={Boolean(colsAnchor)}
            onClose={() => setColsAnchor(null)}
          >
            {COLUMNS.map(col => (
              <MenuItem
                key={col.id}
                onClick={() => setVisibleCols(prev => ({ ...prev, [col.id]: !prev[col.id] }))}
              >
                <Checkbox size="small" checked={visibleCols[col.id]} />
                <ListItemText primary={I18n.t(`AgenticChatFlow.${col.labelKey}`)} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      {attachedTags.length === 0 ? renderEmpty() : renderTable()}
      <ManageTagsDialog
        open={manageOpen}
        tags={allTags || []}
        attachedIds={attachedIds}
        onClose={() => setManageOpen(false)}
        onSaveAttachments={persistTagIds}
        onCreateTag={() => {
          setManageOpen(false);
          openCreate();
        }}
      />
    </Box>
  );
};

TagsPanel.propTypes = {
  agent: object.isRequired,
  skills: arrayOf(object).isRequired,
  tools: arrayOf(object).isRequired,
  allTags: arrayOf(object),
  onSaveAgent: func.isRequired,
  onReloadTags: func,
};

export default TagsPanel;
